import React, {
  useState,
  useCallback,
  useReducer,
  useEffect,
  useContext
} from 'react';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import { UPDATE_FORM } from '../../store/modules/state/formBuilder';
import fetchAPI from '../../utils/fetchAPI';
import FormBuilderShareAccessSettingsDialogView from '../view/FormBuilderShareAccessSettingsDialogView';
import { FormBuilderContext } from './FormBuilderEngine';
import { FormBuilderShareContext } from './FormBuilderShareEngine';

export const FORM_TYPE = {
  REQUIRES_SIGN_IN: 'internal',
  PUBLIC: 'anonymous'
};

/**
 *  @param {Array} oldList - Array of groups updated to data on the server
 *  @param {Array} newList - Array of groups updated to data on local state
 *
 *  @returns {Array} - List of groups that exists on the server but missing from local state
 */
const getGroups2Delete = (oldList, newList) => {
  return (oldList || []).filter(
    og => !(newList || []).find(ng => ng.value === og.value)
  );
};

/**
 *  @param {Array} oldList - Array of groups updated to data on the server
 *  @param {Array} newList - Array of groups updated to data on local state
 *
 *  @returns {Array} - List of groups that don't exists on the server but exists on local state
 */
const getGroups2Add = (oldList, newList) => {
  return getGroups2Delete(newList, oldList);
};

const AccessSettingsReducer = (state, action) => {
  const { type, value, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const reducerInitialState = {
  limitAccessGroups: [],
  initialLimitAccessGroups: []
};

const FormBuilderShareAccessSettingsDialogEngine = ({
  isOpen,
  setIsOpen,
  reducerListener = d => d
}) => {
  const [data, dispatch] = useReducer(
    AccessSettingsReducer,
    reducerInitialState
  );
  const { groups } = useContext(FormBuilderShareContext);
  const { formId, form, autosave } = useContext(FormBuilderContext);
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const [externalUsersLicense, setExternalUsersLicense] = useState(false);
  const [isLimitAccessChecked, setIsLimitAccessChecked] = useState(false);
  const [isAllowExternalChecked, setIsAllowExternalChecked] = useState(
    form.allowExternalUserAccounts
  );
  const [formType, setFormType] = useState(form.sharingType);

  useEffect(() => {
    reducerListener(data);
  }, [data]);

  useEffect(() => {
    fetchAPI(
      `/configurations/v1/modules/license?order=id asc&query=[[{"field":"'module'","op":"=","value":"provisioning"},{"field":"'configName'","op":"=","value":"license"},{"field":"'code'","op":"=","value":"externalusers"}]]`
    )
      .then(res => {
        let a, n;
        a = res && res.configs && res.configs.length && res.configs[0].value;
        n = Number(a) || 0;
        setExternalUsersLicense(!!n);
      })
      .catch(e => {});
  }, []);

  useEffect(() => {
    if (!groups.length) {
      return;
    }
    const queryParam = `[{"field":"'groupType'","op":"=","value":"'security'"}]`;
    fetchAPI(`/forms/v1/forms/${formId}/share/group?query=${queryParam}`).then(
      ({ results }) => {
        if (!results.length) {
          return;
        }

        // results.forEach(lag => {
        //   lag.label = (
        //     groups.find(g => g.value === lag.groupId) || {
        //       label: 'Something went wrong...'
        //     }
        //   ).label;
        // });

        const groupIds = results.map(r => r.groupId).filter(Boolean);

        if (!groupIds.length) {
          return;
        }

        const limitAccessGroups = groups.filter(g =>
          groupIds.includes(g.value)
        );
        if (!limitAccessGroups.length) {
          return;
        }
        dispatch({
          type: 'update',
          payload: {
            limitAccessGroups,
            initialLimitAccessGroups: limitAccessGroups
          }
        });
        setIsLimitAccessChecked(true);
      }
    );
  }, [groups]);

  const onDialogConfirm = useCallback(() => {
    let somethingChanged, newPayload;
    somethingChanged = false;
    newPayload = {};

    if (form.sharingType !== formType) {
      somethingChanged = true;
      newPayload.sharingType = formType;
    }
    if (form.allowExternalUserAccounts !== isAllowExternalChecked) {
      somethingChanged = true;
      newPayload.allowExternalUserAccounts = isAllowExternalChecked;
    }
    /*if (formType === 'internal' && form.allowExternal !== isAllowExternalChecked) {
      somethingChanged = true;
      newPayload.allowExternal = isAllowExternalChecked;
    }
    if (formType !== 'internal' && form.allowExternal) {
      somethingChanged = true;
      newPayload.allowExternal = false;
    }*/
    if (somethingChanged) {
      autosave({ type: UPDATE_FORM, payload: newPayload });
    }

    const groupsUpdatePromises = [];
    let groups2Delete = [],
      groups2Add = [];

    if (!isLimitAccessChecked && data.initialLimitAccessGroups.length) {
      // delete existing groups
      groups2Delete = data.initialLimitAccessGroups;
    }

    if (isLimitAccessChecked) {
      // diff existing and new
      groups2Delete = getGroups2Delete(
        data.initialLimitAccessGroups,
        data.limitAccessGroups
      );
      groups2Add = getGroups2Add(
        data.initialLimitAccessGroups,
        data.limitAccessGroups
      );
    }

    groups2Add.forEach(g => {
      groupsUpdatePromises.push(
        new Promise((res, rej) =>
          fetchAPI(`/forms/v1/forms/${formId}/share/group/${g.value}`, {
            method: 'POST',
            body: { groupType: 'security' }
          })
            .then(res)
            .catch(rej)
        )
      );
    });

    groups2Delete.forEach(g => {
      groupsUpdatePromises.push(
        new Promise((res, rej) =>
          fetchAPI(`/forms/v1/forms/${formId}/share/group/${g.value}`, {
            method: 'DELETE',
            body: { groupType: 'security' }
          })
            .then(res)
            .catch(rej)
        )
      );
    });

    Promise.all(groupsUpdatePromises).then(() => {
      openSnackbar('success', 'automation.snackbar.success.general');
      // update initial state

      const payload = {};

      if (isLimitAccessChecked) {
        payload.initialLimitAccessGroups = data.limitAccessGroups;
        if (!(data.limitAccessGroups || []).length) {
          setIsLimitAccessChecked(false);
        }
      } else {
        payload.limitAccessGroups = [];
        payload.initialLimitAccessGroups = [];
      }
      if (isAllowExternalChecked) {
        payload.allowExternal = true;
      }
      dispatch({
        type: 'update',
        payload
      });
      setIsOpen(false);
    });
  }, [
    form,
    formId,
    autosave,
    formType,
    isLimitAccessChecked,
    isAllowExternalChecked,
    data
  ]);

  const onDialogCancel = useCallback(() => {
    setIsOpen(false);

    // reset state after dialog closing animation is finished
    setTimeout(() => {
      setFormType(form.sharingType);
      // reset state
      dispatch({
        type: 'update',
        payload: { limitAccessGroups: data.initialLimitAccessGroups }
      });
      setIsLimitAccessChecked(!!(data.initialLimitAccessGroups || []).length);
      setIsAllowExternalChecked(form.allowExternalUserAccounts);
    }, 250);
  }, [form, data]);

  const onLimitAccessCheckboxToggle = useCallback(
    e => {
      const isChecked = e.target.checked;
      setIsLimitAccessChecked(isChecked);
    },
    [data, formId]
  );

  const onAllowExternalCheckboxToggle = useCallback(
    e => {
      const isChecked = e.target.checked;
      setIsAllowExternalChecked(isChecked);
    },
    [data, formId]
  );

  const onFormTypeBtnClick = useCallback(type => {
    setFormType(type);
  }, []);

  const onLimitAccessDDChange = useCallback(
    (value, { removedValue }) => {
      dispatch({ type: 'update', payload: { limitAccessGroups: value } });
    },
    [formId]
  );

  const viewProps = {
    isOpen,
    setIsOpen,
    onDialogConfirm,
    onDialogCancel,
    externalUsersLicense,
    isLimitAccessChecked,
    onLimitAccessCheckboxToggle,
    isAllowExternalChecked,
    onAllowExternalCheckboxToggle,
    formType,
    onFormTypeBtnClick,
    groups,
    onLimitAccessDDChange,
    ...data
  };

  return <FormBuilderShareAccessSettingsDialogView {...viewProps} />;
};
export default FormBuilderShareAccessSettingsDialogEngine;
